// Der Sender: bringt den Postausgang an den Server (POST /api/doku/eintrag),
// Stück für Stück, in der Reihenfolge der Nummern (postausgang.ts). Dateien
// (seit E2, Schritt 2) gehen roh an den Datei-Eingang (PUT
// /api/doku/datei/<prüfsumme>, 120 s Zeitlimit) – immer vor dem Foto, das
// sie braucht. Eine Datei hängt an nichts; sie darf deshalb auch dann
// hinaus, wenn ihre Besichtigung gerade wartet. Sagt der Server beim Foto
// „datei-fehlt“, merkt der Sender die Datei neu vor und schickt das Foto
// danach erneut.
//
// Bevor ein Foto oder ein Sprachmemo (seit Schritt 3) zum ersten Mal
// hinausgeht, merkt er sich das daran („abgeschickt“) – ab dann löscht die
// App es auch am Server. „datei-fehlt“ gilt ebenso für Memo und K2-PDF.
//
// Wann er läuft: beim Start der App, wenn das Netz zurückkommt, nach jedem
// Vormerken, wenn die App wieder nach vorne kommt, und alle 30 Sekunden.
// Ohne Netz gar nicht. Es läuft immer nur einer: im Modul ein Merker „läuft“
// (React startet Effekte im Entwicklungsmodus doppelt), dazu – wo es das
// gibt – eine Sperre über alle offenen Fenster der App (navigator.locks).
//
// Was er mit den Antworten macht:
//  - 200 {ok:true}: erledigt – gelöscht, wenn sich die Sache seither nicht
//    geändert hat.
//  - Später erneut, mit wachsendem Abstand: kein Netz, Zeitüberschreitung,
//    5xx, 507 (Platte voll), 429, eine Antwort ohne JSON (Anmeldeportal im
//    WLAN) und „eltern-fehlt“ (höchstens dreimal, dann dauerhaft). Bei allem
//    außer „eltern-fehlt“ hört der Durchgang auf: Es liegt am Netz oder am
//    Server, nicht am Eintrag.
//  - Dauerhaft (400, 403, 404, 409 kennung-vergeben/konflikt/abgeschlossen,
//    413, 501, und was die App selbst nicht gültig bauen kann): Der Eintrag
//    bleibt liegen, die Besichtigung zeigt den Fehler, die Fehlerwache
//    bekommt eine Meldung ohne Kundendaten. Die Besichtigung sendet dann
//    nicht weiter (sonst überholte ein Kind seine Eltern), alle anderen schon.
//  - 401, oder 403 „Gerät gesperrt / wartet / Nutzer gesperrt“: Das betrifft
//    nicht den Eintrag, sondern die Anmeldung. Senden hält an, die Anmeldung
//    fragt neu; der Postausgang bleibt, wie er ist.
//
// Seit E2, Schritt 4 der Abschluss („An das Büro senden“):
//  - 200: Die Besichtigung ist im Büro (`aufnahmeImBuero`), ihre Fotos und
//    Memos kommen vom Gerät (`dateienFreigeben`). Ebenso, wenn danach noch
//    eine Datei einer Besichtigung hochgeht, die schon im Büro ist (K2).
//  - 409 „unvollstaendig“: Die App heilt sich selbst – was am Server fehlt,
//    schickt sie nach, was dort zu viel ist, löscht sie, und der Abschluss
//    stellt sich danach hinten neu an (postausgang.ts, `abschlussHeilen`).
//    Nach dem dritten Heilversuch gilt das als dauerhaft; ebenso eine
//    Station „zu viel“, die lässt sich nicht löschen.
//
// Seit Schritt 5 „Etwas nachreichen“, genauso: Bevor „nachtrag“ zum ersten
// Mal hinausgeht, merkt er sich das am Nachtrag („abgeschickt“ – ab dann
// schließt „Nachtrag abbrechen“ ihn am Server, statt ihn nur am Gerät zu
// vergessen). 200 auf „nachtrag-abgeschlossen“: Der Nachtrag ist im Büro
// (`nachtragImBuero`), seine Fotos und Memos kommen vom Gerät. Bei
// „unvollstaendig“ heilt er sich wie der erste Abschluss.
//
// Seit E3 (S6, S7) Fragen, Antworten, „erledigt“ und der Montagetermin – wie
// alle anderen Einträge, mit denselben Antworten: 409 „eltern-fehlt“ (die
// Frage, das Foto ist noch nicht am Server) später erneut, „konflikt“
// dauerhaft. Bei 200 vermerkt er im selben Zug mit dem Austragen, dass der
// Server es hat (gespraech.ts, `amServerVermerken`) – daran erkennt das
// Auffrischen, wann die Kopie vom Server es sicher kennt.

import { eintragEingabe, type DateiAntwort, type Eintrag, type EintragAntwort } from "@happy-energy/kern";
import { ApiFehler, dateiSenden, fehlerMelden, OhneVerbindung, ohneNetz, senden } from "@happy-energy/oberflaeche";
import { speicher, type PostArt, type PostEintrag } from "./speicher.ts";
import {
  abschlussHeilen,
  abstaendeVergessen,
  aufnahmeFehlerAktualisieren,
  austragen,
  beiNeuemEintrag,
  dateiFinden,
  dateiIstAmServer,
  eintragBauen,
  erledigt,
  kennungenZaehlen,
  kennungsListenLesen,
  vormerken,
} from "./postausgang.ts";
import { aufnahmeImBuero, nachtragImBuero } from "./arbeitskopie.ts";
import { dateienFreigeben } from "./dateien.ts";
import { amServerVermerken } from "./gespraech.ts";
import { vomBueroGeloescht } from "./wegraeumen.ts";

/** Wie oft „eltern-fehlt“ erneut versucht wird, bevor es als dauerhaft gilt. */
export const ELTERN_FEHLT_HOECHSTENS = 3;
/** Wie oft die App nachschickt, was dem Server beim Abschluss fehlt, bevor es als dauerhaft gilt. */
export const HEILVERSUCHE_HOECHSTENS = 3;
/** Alle so viele Millisekunden ein Durchgang, solange die App offen ist. */
export const TAKT = 30_000;
/**
 * Netz zurück oder App wieder vorne: sofort senden – und kurz danach noch
 * ein paar Mal, denn nach dem Flugmodus brauchen Tunnel und Namensauflösung
 * oft noch Sekunden (Augentest am iPhone, 19.10.2026: „2 wartet“ blieb eine
 * Weile stehen, weil der erste Versuch zu früh kam und der Abstand wuchs).
 */
export const NACHFASSEN_MS = [3_000, 10_000, 30_000] as const;
/** Wie lange das Senden nach „nicht angemeldet“ ruht. */
export const ANMELDE_PAUSE = 60_000;

/** Der wachsende Abstand: 5 s, 10 s, 20 s … höchstens 2 Minuten. */
export function abstand(versuche: number): number {
  return Math.min(120_000, 5_000 * 2 ** Math.max(0, versuche - 1));
}

/** Die Namen der Arten für Menschen – in Fehlermeldungen an der Besichtigung. */
const ART_NAMEN: Record<PostArt, string> = {
  datei: "Eine Datei",
  aufnahme: "Kunde und Objekt",
  station: "Eine Station",
  notiz: "Eine Notiz",
  laenge: "Eine Länge",
  material: "Ein Material",
  foto: "Ein Foto",
  memo: "Ein Sprachmemo",
  k2: "Die Dachplanung",
  geloescht: "Ein Löschen",
  abgeschlossen: "Der Abschluss",
  nachtrag: "Der Nachtrag",
  "nachtrag-abgeschlossen": "Der Abschluss des Nachtrags",
  frage: "Eine Frage",
  antwort: "Eine Antwort",
  "frage-stand": "„Erledigt“ an einer Frage",
  montagetermin: "Der Montagetermin",
  verwerfen: "Das Verwerfen",
};

/** Die Dateien, an denen ein Eintrag hängt: Foto und Memo eine, die Dachplanung mit K2 bis zu zwei (seit E3c, S5: PDF und Excel-Report). */
export function dateienDes(eintrag: Eintrag): string[] {
  if (eintrag.art === "foto" || eintrag.art === "memo") return [eintrag.pruefsumme];
  if (eintrag.art === "k2") return [eintrag.pruefsumme, eintrag.excel].filter((s): s is string => typeof s === "string");
  return [];
}

/** 403 vom Wächter, die die Anmeldung betreffen – nicht den Eintrag. */
const ANMELDUNG_KLAEREN = new Set(["geraet-wartet", "geraet-gesperrt", "nutzer-gesperrt"]);

export interface SenderEinstellung {
  /** An die Fehlerwache – bekommt nur Art, Status und Grund, nie Inhalte. */
  melden: (fehler: Error) => void;
  /** Der Server sagt „nicht angemeldet“ o. Ä.: Die Anmeldung soll neu fragen. */
  anmeldungNoetig: () => void;
  /** Die Uhr – für Tests austauschbar. */
  jetzt: () => number;
}

type Weiter = "weiter" | "aufnahme-anhalten" | "halt";

/** Ein Fehler beim Senden, wie er an die Fehlerwache geht – ohne Inhalte. */
export class SendeFehler extends Error {
  constructor(meldung: string) {
    super(meldung);
    this.name = "Postausgang";
  }
}

export class Sender {
  private laeuft = false;
  private nochmal = false;
  private pausiertBis = 0;
  private einstellung: SenderEinstellung;

  constructor(einstellung: Partial<SenderEinstellung> = {}) {
    this.einstellung = {
      melden: (f) => fehlerMelden("doku", f),
      anmeldungNoetig: () => undefined,
      jetzt: () => Date.now(),
      ...einstellung,
    };
  }

  einstellen(einstellung: Partial<SenderEinstellung>): void {
    this.einstellung = { ...this.einstellung, ...einstellung };
  }

  /**
   * Startet das regelmäßige Senden (App-Start, Netz zurück, nach jedem
   * Vormerken, App wieder vorne, alle 30 s). Kommt das Netz zurück oder die
   * App wieder nach vorne, wartet nichts mehr auf seinen Abstand: Es wird
   * sofort gesendet und kurz danach nachgefasst (NACHFASSEN_MS). Gibt die
   * Funktion zum Anhalten zurück.
   */
  starten(): () => void {
    this.pausiertBis = 0;
    const anstossen = () => void this.senden();
    const frisch = () => void abstaendeVergessen().then(anstossen, anstossen);
    let nachfassen: number[] = [];
    const netzZurueck = () => {
      for (const u of nachfassen) window.clearTimeout(u);
      frisch();
      nachfassen = NACHFASSEN_MS.map((ms) => window.setTimeout(frisch, ms));
    };
    const vorne = () => {
      if (document.visibilityState === "visible") netzZurueck();
    };
    const abmelden = beiNeuemEintrag(anstossen);
    window.addEventListener("online", netzZurueck);
    document.addEventListener("visibilitychange", vorne);
    const uhr = window.setInterval(anstossen, TAKT);
    anstossen();
    return () => {
      abmelden();
      window.removeEventListener("online", netzZurueck);
      document.removeEventListener("visibilitychange", vorne);
      window.clearInterval(uhr);
      for (const u of nachfassen) window.clearTimeout(u);
    };
  }

  /**
   * Ein Durchgang durch den Postausgang. Läuft schon einer, wird er danach
   * einfach noch einmal gemacht – so geht nichts verloren, was während des
   * Sendens dazukam.
   */
  async senden(): Promise<void> {
    if (this.laeuft) {
      this.nochmal = true;
      return;
    }
    this.laeuft = true;
    try {
      do {
        this.nochmal = false;
        await this.mitSperre(() => this.durchgang());
      } while (this.nochmal && !ohneNetz());
    } finally {
      this.laeuft = false;
    }
  }

  /** Nur ein Fenster der App sendet: Hält ein anderes die Sperre, lässt dieses es bleiben. */
  private async mitSperre(arbeit: () => Promise<void>): Promise<void> {
    const sperren = typeof navigator !== "undefined" ? navigator.locks : undefined;
    if (!sperren?.request) return arbeit();
    await sperren.request("he-postausgang", { ifAvailable: true }, async (sperre) => {
      if (sperre) await arbeit();
    });
  }

  private async durchgang(): Promise<void> {
    const eintraege = await speicher.postausgang.orderBy("nr").toArray();
    // Besichtigungen, die in diesem Durchgang nicht weitersenden – ihre Reihenfolge bleibt.
    const angehalten = new Set<string>();
    for (const p of eintraege) {
      if (ohneNetz() || this.pausiertBis > this.einstellung.jetzt()) return;
      // Eine Datei hängt an nichts – sie darf auch dann, wenn ihre Besichtigung wartet.
      if (angehalten.has(p.aufnahmeId) && p.art !== "datei") continue;
      if (p.fehler !== null || p.naechsterVersuch > this.einstellung.jetzt()) {
        angehalten.add(p.aufnahmeId);
        continue;
      }
      const weiter = await this.einenSenden(p);
      if (weiter === "halt") return;
      if (weiter === "aufnahme-anhalten") angehalten.add(p.aufnahmeId);
    }
  }

  private async einenSenden(p: PostEintrag): Promise<Weiter> {
    if (p.art === "datei") return this.dateiHochladen(p);
    let eintrag: Eintrag;
    try {
      const gebaut = await eintragBauen(p);
      // Die Sache gibt es so nicht mehr – nichts zu senden.
      if (gebaut === null) {
        await erledigt(p);
        return "weiter";
      }
      const geprueft = eintragEingabe.safeParse(gebaut);
      if (!geprueft.success) {
        // Nur die Stelle und die Regel – nie den Wert.
        const erste = geprueft.error.issues[0];
        const stelle = erste ? [erste.path.join("."), erste.message].filter(Boolean).join(": ") : "ungültig";
        await this.dauerhaft(p, `${ART_NAMEN[p.art]} ist am Gerät ungültig (${stelle}).`, `${p.art} am Gerät ungültig – ${stelle}`);
        return "aufnahme-anhalten";
      }
      eintrag = geprueft.data;
    } catch (fehler) {
      const text = fehler instanceof Error ? fehler.message : String(fehler);
      await this.dauerhaft(p, `${ART_NAMEN[p.art]} ließ sich nicht senden.`, `${p.art} nicht gebaut – ${text}`);
      return "aufnahme-anhalten";
    }

    // Ein Foto, das es inzwischen nicht mehr gibt, geht nicht mehr hinaus.
    if (!(await this.alsAbgeschicktMerken(eintrag))) {
      await erledigt(p);
      return "weiter";
    }

    let antwort: EintragAntwort | null;
    try {
      antwort = await senden<EintragAntwort | null>("/api/doku/eintrag", eintrag);
    } catch (fehler) {
      return this.fehlerBehandeln(p, fehler, eintrag);
    }
    // Nur ein ausdrückliches „ok“ zählt.
    if (antwort?.ok !== true) {
      await this.spaeter(p);
      return "halt";
    }
    // Fragen und Termin (seit E3): im selben Zug vermerken, dass der Server sie hat.
    const jetzt = new Date(this.einstellung.jetzt());
    await erledigt(p, () => amServerVermerken(eintrag, jetzt));
    // Der Server hat alles: im Büro – auch, wenn der Eintrag inzwischen neu vorgemerkt war.
    if (eintrag.art === "abgeschlossen" || eintrag.art === "nachtrag-abgeschlossen") await this.imBuero(eintrag);
    return "weiter";
  }

  /**
   * Die Besichtigung (oder ihr Nachtrag, seit Schritt 5) ist im Büro: so
   * vermerken, dann die Dateien vom Gerät nehmen, die jetzt beim Büro sind.
   * Geht das schief (Speicher am Gerät), bleibt es beim Alten – die
   * Fehlerwache erfährt es, ohne Inhalte.
   */
  private async imBuero(eintrag: Eintrag): Promise<void> {
    const jetzt = new Date(this.einstellung.jetzt());
    try {
      if (eintrag.art === "nachtrag-abgeschlossen") await nachtragImBuero(eintrag.kennung, jetzt);
      else await aufnahmeImBuero(eintrag.aufnahmeId, jetzt);
      await dateienFreigeben(eintrag.aufnahmeId);
    } catch (fehler) {
      this.einstellung.melden(new SendeFehler(`${eintrag.art}: im Büro nicht vermerkt – ${fehler instanceof Error ? fehler.name : "unbekannt"}`));
    }
  }

  /**
   * Vor dem ersten Hinausgehen eines Fotos oder Memos: daran vermerken, dass
   * es ab jetzt am Server sein kann – in einem Zug mit dem Nachsehen, ob es
   * das noch gibt. Wird es danach gelöscht, geht „geloescht“ hinaus; wurde
   * es davor gelöscht, geht es gar nicht erst hinaus (false). Ebenso der
   * Nachtrag selbst (seit Schritt 5): Ab jetzt kann er am Server offen sein
   * – abgebrochen wird er dann dort; wurde er davor abgebrochen, geht er
   * nicht mehr hinaus.
   */
  private async alsAbgeschicktMerken(eintrag: Eintrag): Promise<boolean> {
    if (eintrag.art === "nachtrag") {
      return speicher.transaction("rw", speicher.nachtraege, async () => {
        const n = await speicher.nachtraege.get(eintrag.kennung);
        if (!n) return false;
        if (!n.abgeschickt) await speicher.nachtraege.update(n.id, { abgeschickt: true });
        return true;
      });
    }
    if (eintrag.art === "foto") {
      return speicher.transaction("rw", speicher.fotos, async () => {
        const f = await speicher.fotos.get(eintrag.kennung);
        if (!f) return false;
        if (!f.abgeschickt) await speicher.fotos.update(f.id, { abgeschickt: true });
        return true;
      });
    }
    if (eintrag.art === "memo") {
      return speicher.transaction("rw", speicher.memos, async () => {
        const m = await speicher.memos.get(eintrag.kennung);
        if (!m) return false;
        if (!m.abgeschickt) await speicher.memos.update(m.id, { abgeschickt: true });
        return true;
      });
    }
    return true;
  }

  /** Eine Datei an den Datei-Eingang – roh, benannt nach ihrer Prüfsumme. */
  private async dateiHochladen(p: PostEintrag): Promise<Weiter> {
    const datei = await dateiFinden(p.kennung);
    // Liegt sie nicht mehr am Gerät (Foto gelöscht), gibt es nichts hochzuladen.
    if (!datei) {
      await erledigt(p);
      return "weiter";
    }
    try {
      const antwort = await dateiSenden<DateiAntwort | null>(`/api/doku/datei/${p.kennung}`, datei);
      if (antwort?.ok !== true) {
        await this.spaeter(p);
        return "halt";
      }
      await dateiIstAmServer(p.kennung);
      // Gleiche Prüfsumme heißt gleicher Inhalt – auch wenn sie inzwischen noch einmal vorgemerkt wurde, ist sie da.
      await austragen("datei", p.kennung);
    } catch (fehler) {
      return this.fehlerBehandeln(p, fehler);
    }
    // Ist die Besichtigung schon im Büro (das K2-PDF kam nach dem Senden), darf die Datei jetzt vom Gerät.
    try {
      if ((await speicher.aufnahmen.get(p.aufnahmeId))?.stand === "im_buero") await dateienFreigeben(p.aufnahmeId);
    } catch (fehler) {
      this.einstellung.melden(new SendeFehler(`datei: nach dem Senden nicht freigegeben – ${fehler instanceof Error ? fehler.name : "unbekannt"}`));
    }
    return "weiter";
  }

  /**
   * Der Server hat die Datei zu einem Foto, Memo oder K2-PDF nicht (mehr):
   * neu vormerken, solange sie am Gerät liegt. false: Sie liegt auch hier
   * nicht mehr.
   */
  private async dateiNachreichen(pruefsumme: string, aufnahmeId: string): Promise<boolean> {
    await dateiIstAmServer(pruefsumme, false);
    if (!(await dateiFinden(pruefsumme))) return false;
    await vormerken("datei", pruefsumme, aufnahmeId);
    return true;
  }

  private async fehlerBehandeln(p: PostEintrag, fehler: unknown, eintrag?: Eintrag): Promise<Weiter> {
    if (fehler instanceof OhneVerbindung) {
      await this.spaeter(p);
      return "halt";
    }
    if (!(fehler instanceof ApiFehler)) {
      // Unerwartet (z. B. der Speicher am Gerät): später erneut, und Bescheid sagen.
      await this.spaeter(p);
      this.einstellung.melden(new SendeFehler(`${p.art}: unerwarteter Fehler beim Senden – ${fehler instanceof Error ? fehler.name : "unbekannt"}`));
      return "halt";
    }

    const { status, grund } = fehler;
    if (status === 401 || (status === 403 && grund !== null && ANMELDUNG_KLAEREN.has(grund))) {
      this.pausiertBis = this.einstellung.jetzt() + ANMELDE_PAUSE;
      this.einstellung.anmeldungNoetig();
      return "halt";
    }
    // Netz oder Server: eine Seite statt JSON mit 2xx (Anmeldeportal), zu viele Anfragen,
    // Server-Fehler, Platte voll. Nicht: 413 von Caddy (auch als Text) und 501 („kommt
    // in einem späteren Schritt“) – die sind dauerhaft.
    if (status < 400 || status === 408 || status === 429 || (status >= 500 && status !== 501)) {
      await this.spaeter(p);
      return "halt";
    }
    // Das Büro hat die Besichtigung gelöscht (seit E3c, S6): Die Kopie samt Postausgang kommt sofort weg – nicht erst
    // beim nächsten Abgleich der Startseite (wegraeumen.ts). Danach gibt es für sie nichts mehr zu senden.
    if (status === 409 && grund === "geloescht") {
      try {
        await vomBueroGeloescht(p.aufnahmeId, new Date(this.einstellung.jetzt()));
      } catch (f) {
        await this.spaeter(p);
        this.einstellung.melden(new SendeFehler(`${p.art}: nach „geloescht“ nicht weggeräumt – ${f instanceof Error ? f.name : "unbekannt"}`));
      }
      return "aufnahme-anhalten";
    }
    // „verwerfen“, aber das Büro arbeitet schon mit der Anlage (seit der Code-Prüfung E3c, Befund 3): Am Server bleibt
    // sie, am Gerät ist sie weg, wie der Verkäufer wollte. Erledigt – und einmal der Hinweis auf der Startseite, im
    // selben Zug. Kein Fehler, also nichts an die Fehlerwache.
    if (p.art === "verwerfen" && status === 409 && grund === "buero-arbeitet") {
      const wann = new Date(this.einstellung.jetzt()).toISOString();
      const was = p.verworfen ?? { name: "Eine verworfene Besichtigung", ort: "" };
      await speicher.transaction("rw", speicher.postausgang, speicher.aufnahmen, speicher.hinweise, async () => {
        if (await erledigt(p)) await speicher.hinweise.add({ art: "buero-arbeitet", name: was.name, ort: was.ort, wann });
      });
      return "weiter";
    }
    // „verwerfen“ abgewiesen (schon gesendet, gehört jemand anderem): Am Gerät ist die Besichtigung längst weg, und
    // löschen kann sie dann nur das Büro – der Eintrag ist erledigt, die Fehlerwache erfährt es.
    if (p.art === "verwerfen") {
      await erledigt(p);
      this.einstellung.melden(new SendeFehler(`verwerfen abgewiesen – ${status}${grund ? ` ${grund}` : ""}`));
      return "weiter";
    }
    if (status === 409 && grund === "eltern-fehlt") {
      if (p.elternFehlt + 1 > ELTERN_FEHLT_HOECHSTENS) {
        await this.dauerhaft(p, `${ART_NAMEN[p.art]} hängt an etwas, das am Server fehlt.`, `${p.art} abgewiesen – 409 eltern-fehlt (${p.elternFehlt + 1}. Mal)`);
      } else {
        await this.spaeter(p, { elternFehlt: p.elternFehlt + 1 });
      }
      return "aufnahme-anhalten";
    }
    // Die Datei fehlt noch: Die schickt die App zuerst nach (ab Schritt 2).
    if (status === 409 && grund === "datei-fehlt") {
      // Foto, Memo und K2 tragen die Prüfsumme ihrer Datei – K2 seit E3c (S5) bis zu zwei (PDF, Excel-Report):
      // Welche fehlt, sagt der Server nicht; nachgereicht wird jede, die noch am Gerät liegt. Liegt keine mehr
      // hier, ist es dauerhaft.
      const summen = eintrag ? dateienDes(eintrag) : [];
      let nachgereicht = summen.length === 0;
      for (const summe of summen) if (await this.dateiNachreichen(summe, p.aufnahmeId)) nachgereicht = true;
      if (!nachgereicht) {
        await this.dauerhaft(p, `${ART_NAMEN[p.art]}: Die Datei dazu ist weder am Gerät noch am Server.`, `${p.art} abgewiesen – 409 datei-fehlt, Datei nicht am Gerät`);
        return "aufnahme-anhalten";
      }
      await this.spaeter(p);
      return "aufnahme-anhalten";
    }
    // Beim Abschluss fehlt etwas oder ist zu viel: nachschicken, löschen, Abschluss hinten neu (ab Schritt 4).
    if (status === 409 && grund === "unvollstaendig" && (p.art === "abgeschlossen" || p.art === "nachtrag-abgeschlossen")) {
      return this.abschlussHeilen(p, fehler);
    }

    // Dauerhaft: 400, 403, 404, 409 (vergeben, Konflikt, abgeschlossen), 413, 501 …
    const fuerMenschen = `${ART_NAMEN[p.art]} wurde vom Server abgelehnt: ${fehler.message}`;
    // Bei 400 steht in der Meldung nur die Stelle und die Regel (routen.ts), nie ein Wert.
    const fuerWache = `${p.art} abgewiesen – ${status}${grund ? ` ${grund}` : ""}${status === 400 ? ` (${fehler.message})` : ""}`;
    await this.dauerhaft(p, fuerMenschen, fuerWache);
    return "aufnahme-anhalten";
  }

  /**
   * „unvollstaendig“ beim Abschluss: heilen, solange es geht. Nicht heilbar
   * ist eine Station „zu viel“ (Stationen löscht kein Eintrag) und alles
   * nach dem dritten Heilversuch – dann ein dauerhafter Fehler. An die
   * Fehlerwache gehen nur Art, Status, Grund und Anzahlen, nie Kennungen.
   */
  private async abschlussHeilen(p: PostEintrag, fehler: ApiFehler): Promise<Weiter> {
    const fehlt = kennungsListenLesen(fehler.daten["fehlt"]);
    const zuViel = kennungsListenLesen(fehler.daten["zuViel"]);
    const anzahlen = `fehlt ${kennungenZaehlen(fehlt)}, zu viel ${kennungenZaehlen(zuViel)}`;
    const versuche = p.heilversuche ?? 0;
    if (zuViel.station.length > 0) {
      await this.dauerhaft(
        p,
        "Der Abschluss passt nicht zum Server: Dort ist eine Station zu viel.",
        `${p.art} abgewiesen – 409 unvollstaendig, Station zu viel (${anzahlen})`,
      );
      return "aufnahme-anhalten";
    }
    if (versuche >= HEILVERSUCHE_HOECHSTENS) {
      await this.dauerhaft(
        p,
        "Der Abschluss passt nicht zum Server.",
        `${p.art} abgewiesen – 409 unvollstaendig nach ${versuche} Heilversuchen (${anzahlen})`,
      );
      return "aufnahme-anhalten";
    }
    try {
      await abschlussHeilen(p, fehlt, zuViel);
    } catch (f) {
      // Der Speicher am Gerät hakt: später noch einmal – der Server sagt dann wieder, was fehlt.
      await this.spaeter(p);
      this.einstellung.melden(new SendeFehler(`${p.art}: Heilen fehlgeschlagen – ${f instanceof Error ? f.name : "unbekannt"}`));
    }
    return "aufnahme-anhalten";
  }

  /** Später erneut – mit wachsendem Abstand. */
  private async spaeter(p: PostEintrag, zusatz: Partial<PostEintrag> = {}): Promise<void> {
    if (p.nr === undefined) return;
    const versuche = p.versuche + 1;
    await speicher.postausgang.update(p.nr, { versuche, naechsterVersuch: this.einstellung.jetzt() + abstand(versuche), ...zusatz });
  }

  /**
   * Dauerhaft: Der Eintrag bleibt mit dem Fehler liegen, die Besichtigung
   * zeigt ihn. Hat sich die Sache inzwischen geändert, gilt der Fehler nur
   * für den alten Stand – dann wird der neue versucht.
   */
  private async dauerhaft(p: PostEintrag, fuerMenschen: string, fuerWache: string): Promise<void> {
    if (p.nr === undefined) return;
    const nr = p.nr;
    await speicher.transaction("rw", speicher.postausgang, speicher.aufnahmen, async () => {
      const jetzt = await speicher.postausgang.get(nr);
      if (!jetzt || jetzt.version !== p.version) return;
      await speicher.postausgang.update(nr, { fehler: fuerMenschen, versuche: p.versuche + 1 });
      await aufnahmeFehlerAktualisieren(p.aufnahmeId);
    });
    this.einstellung.melden(new SendeFehler(fuerWache));
  }
}

/** Der eine Sender der App. */
export const sender = new Sender();
